import buildGraphQLProvider, { buildQuery } from 'ra-data-graphql-simple';
import { setContext } from '@apollo/client/link/context';
import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';
import gql from 'graphql-tag';
import { getEnv } from './utils/getEnv';
import { analyticsTrackEvent, segmentMiddleware } from './utils/segment';


export const retrieveGraphqlURI = _ => {
  const env = getEnv();
  if (env === 'local') return 'http://localhost:4000/private';
  if (env === 'staging') return 'https://graphql.staging.matrak.com.au/private';
  if (env === 'sandbox') return 'https://rest-sandbox.matrak.com.au/private';
  if (env === 'prod') return 'https://graphql.matrak.com.au/private';
  if (env === 'feature') return 'https://feature.matrak.com.au/private';
}

const uri = retrieveGraphqlURI();
const cache = new InMemoryCache();
const httpLink = new HttpLink({
  uri,
});

const authLink = setContext((_, {headers}) => {
  // get the authentication token from local storage if it exists
  const token = localStorage.getItem('token');
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      // authorization: token || '',
      authorization: token,
    },
  };
});


export const client = new ApolloClient({
  cache,
  link: authLink.concat(httpLink),
});

const overrideParams = (params, fetchType, resource) => {
  if (params && params.sort && params.sort.order) {
    params = {
      ...params,
      sort: {
        ...params.sort,
        order: params.sort.order.toLowerCase(),
      },
    };
  }

  if (fetchType === 'UPDATE') {
    // params[`update${resource}sInput`] = {...params['data']};
    // Only send through the updated values
    let updateParams = {};
    const prevData = params['previousData'];
    const newData = params['data'];
    for (const key of Object.keys(newData)) {
      if (typeof prevData[key] !== 'object' && prevData[key] !== newData[key]) {
        updateParams[key] = newData[key]
      }
    }
    params['data'][`update${resource}Input`] = updateParams;
  }
  return params
};


const getCompanyId = () => {
  const url = window.location.href;
  const regex = /(?<=\/Company\/)\d+(?=\/show)/;
  const match = url.match(regex);
  const companyId = match ? Number(match[0]) : null;
  return companyId;
}
const myBuildQuery = (introspection) => (fetchType, resource, params) => {
  console.log(`Resource: ${resource} fetchType ${fetchType}`);

  // Remove value field from Record and project access from users.
  // It breaks introspection TODO: Where do we get introspection from, it's prob better to do it there ay.
  let recordTypedef = introspection.types.find((type) => type.name === 'Record');
  const valueFieldIndex = recordTypedef.fields.findIndex((field) => field.name === "value");
  if (valueFieldIndex > 0) {
    const newFields = [
      ...recordTypedef.fields.slice(0, valueFieldIndex),
      ...recordTypedef.fields.slice(valueFieldIndex + 1),
    ];
    recordTypedef = {
      ...recordTypedef,
      fields: newFields,
    };
  }
  let userdef = introspection.types.find((type) => type.name === "User");
  const accessFieldIndex = userdef.fields.findIndex((field) => field.name === "projectAccess");
  if (accessFieldIndex > 0) {
    const newFields = [
      ...userdef.fields.slice(0, accessFieldIndex),
      ...userdef.fields.slice(accessFieldIndex + 1),
    ];
    userdef = {
      ...userdef,
      fields: newFields,
    };
  }

  const overriddenParams = overrideParams(params, fetchType, resource);
  const builtQuery = buildQuery(introspection)(fetchType, resource, overriddenParams);

  segmentMiddleware(fetchType, resource, params);

  if (resource === 'Project' && (fetchType === 'GET_LIST' || fetchType === 'GET_MANY')) {
    const companyId = getCompanyId();

    // Determine if companyId should be included in the variables and query
    const includeCompanyId = fetchType !== 'GET_LIST';

    // Construct the query dynamically based on fetchType
    let query = gql`
        query allProjects($page: Int, $perPage: Int, $sortField: String, $sortOrder: String, $filter: ProjectFilter${includeCompanyId ? `, $companyId: Int` : ''}) {
            items: allProjects(page: $page, perPage: $perPage, sortField: $sortField, sortOrder: $sortOrder, filter: $filter) {
                id
                name
                address
                owner
                contact_details
                comments
                pricing_tier
                start_date
                end_date
                created_at
                updated_at
                classification
                hubspot_id
                bci_id
                is_archived
                archived_at
                archived_by {
                    id
                    __typename
                }
                companies {
                    id,
                    __typename
                }
                groups {
                    id
                    __typename
                }
                numberOfAdmins
                numberOfEndUsers
                ${includeCompanyId ? `
                    totalRecords
                    totalBillableRecords(companyId: $companyId)
                    totalCollaborators(companyId: $companyId)
                    isComplete(companyId: $companyId)
                ` : ''}
                __typename
            }
            total: _allProjectsMeta(filter: $filter) {
                count
                __typename
            }
        }
    `;

    return {
      ...builtQuery,
      variables: {
        ...builtQuery.variables,
        ...(includeCompanyId && {companyId}),
      },
      query,
    };
  }

  if (resource === 'Sheet' && fetchType === 'GET_LIST') {
    return {
      ...builtQuery,
      query: gql`
          query allSheets($page: Int, $perPage: Int, $sortField: String, $sortOrder: String, $filter: AllSheetsFilter){
              allSheets(page: $page, perPage: $perPage, sortField: $sortField, sortOrder: $sortOrder, filter:$filter) {
                  id
                  title
                  order
                  project {
                      id
                      name
                  }
                  user {
                      id
                      username
                  }
              }
              _allSheetsMeta(page: $page, perPage: $perPage, sortField: $sortField, sortOrder: $sortOrder, filter:$filter){
                  count
              }
          }`,
      parseResponse: (res) => {

        const data = {
          data: res.data.allSheets,
          total: res.data._allSheetsMeta.count
        };
        return data;
      },
    }
  }

  if (resource === 'User' && fetchType === 'GET_MANY') {
    return {
      ...builtQuery,
      query: gql`
          query allUsers($page: Int, $perPage: Int, $sortField: String, $sortOrder: String, $filter: UserFilter){
              allUsers(page: $page, perPage: $perPage, sortField: $sortField, sortOrder: $sortOrder, filter:$filter) {
                  id
                  username
              }
              _allUsersMeta(page: $page, perPage: $perPage, sortField: $sortField, sortOrder: $sortOrder, filter:$filter){
                  count
              }
          }`,
      parseResponse: (res) => {
        const data = {
          data: res.data.allUsers,
          total: res.data._allUsersMeta.count
        };
        return data;
      },
    }
  }

  if (resource === 'User' && fetchType === 'GET_LIST') {
    return {
      ...builtQuery,
      query: gql`
          query allUsers($page: Int, $perPage: Int, $sortField: String, $sortOrder: String, $filter: UserFilter){
              allUsers(page: $page, perPage: $perPage, sortField: $sortField, sortOrder: $sortOrder, filter:$filter) {
                  id
                  username
                  email
                  company {
                      id
                      name
                  }
                  global_access
                  enabled
                  created_at
                  updated_at
              }
              _allUsersMeta(page: $page, perPage: $perPage, sortField: $sortField, sortOrder: $sortOrder, filter:$filter){
                  count
              }
          }`,
      parseResponse: (res) => {
        const data = {
          data: res.data.allUsers,
          total: res.data._allUsersMeta.count
        };
        return data;
      },
    }
  }

  if (resource === 'Record' && fetchType === 'GET_MANY') {
    return {
      ...builtQuery,
      query: gql`
          query allRecords($page: Int, $perPage: Int, $sort: RecordSort, $filter: RecordFilter){
              allRecords(page: $page, perPage: $perPage, sort: $sort, filter:$filter) {
                  id
                  record_id
                  record_type {
                      id
                  }
                  values {
                      id
                      value
                      description {
                          id
                      }
                  }
                  project {
                      id
                  }
                  childLink {
                      id
                  }
                  parentLink {
                      id
                  }
              }
              _allRecordsMeta(page: $page, perPage: $perPage, sort: $sort, filter:$filter){
                  count
              }
          }`,
      parseResponse: (res) => {
        const data = {
          data: res.data.allRecords,
          total: res.data._allRecordsMeta.count
        };
        return data;
      },
    }
  }

  if (resource === 'Record' && fetchType === 'GET_LIST') {
    return {
      ...builtQuery,
      query: gql`
          query allRecords($page: Int, $perPage: Int, $sort: RecordSort, $filter: RecordFilter){
              allRecords(page: $page, perPage: $perPage, sort: $sort, filter:$filter) {
                  id
                  record_id
                  record_type {
                      id
                  }
                  values {
                      id
                      value
                      description {
                          id
                      }
                  }
                  project {
                      id
                  }
                  childLink {
                      id
                  }
                  parentLink {
                      id
                  }
              }
              _allRecordsMeta(page: $page, perPage: $perPage, sort: $sort, filter:$filter){
                  count
              }
          }`,
      parseResponse: (res) => {
        const data = {
          data: res.data.allRecords,
          total: res.data._allRecordsMeta.count
        };
        return data;
      },
    }
  }

  if (resource === 'Record' && fetchType === 'GET_ONE') {
    return {
      ...builtQuery,
      query: gql`
          query Record($id: Int){
              Record(id: $id) {
                  id
                  record_id
                  record_type {
                      id
                  }
                  values {
                      id
                      value
                      description {
                          id
                      }
                  }
                  project {
                      id
                  }
                  childLink {
                      id
                  }
                  parentLink {
                      id
                  }
              }
          }`,
      parseResponse: (res) => {
        const data = {
          data: res.data.Record,
        };
        return data;
      },
    }
  }

  if (resource === 'Sheet' && fetchType === 'GET_ONE') {
    return {
      ...builtQuery,
      query: gql`
          query Sheet($id: Int!){
              Sheet(id: $id) {
                  id
                  title
                  order
                  project {
                      id
                      name
                  }
                  user {
                      id
                      username
                  }
              }
          }`,
      parseResponse: (res) => {
        const data = {
          data: res.data.Sheet,
        };
        return data;
      },
    }
  }

  // Helper function to uppercase description types in config
  const normalizeConfigEnums = (config) => {
    if (!config || typeof config !== 'object') return config;

    const normalized = { ...config };

    if (normalized.schema?.recordTypes) {
      normalized.schema.recordTypes = normalized.schema.recordTypes.map(recordType => ({
        ...recordType,
        descriptions: recordType.descriptions?.map(desc => ({
          ...desc,
          type: desc.type?.toUpperCase() || desc.type
        })) || []
      }));
    }

    return normalized;
  };

  if (resource === 'ImportConfig' && fetchType === 'UPDATE') {
    const {variables} = builtQuery;
    const newVariables = {
      ...variables,
      updateImportConfigInput: {
        name: params.data.name,
        config: normalizeConfigEnums(params.data.config),
        configRaw: params.data.configRaw,
        projectId: params.data.projectId,
        companyId: params.data.companyId,
        userId: params.data.userId,
      },
    };

    return {
      ...builtQuery,
      variables: newVariables,
      query: gql`
        mutation updateImportConfig($id: Int!, $updateImportConfigInput: UpdateImportConfigInput!) {
          updateImportConfig(id: $id, updateImportConfigInput: $updateImportConfigInput) {
            id
            name
            configRaw
            company {
              id
              name
            }
            project {
              id
              name
            }
            createdBy {
              id
              username
              first_name
              last_name
              email
            }
            createdAt
            updatedAt
          }
        }
      `,
      parseResponse: ({data}) => {
        if (data.updateImportConfig) {
          const record = {
            ...data.updateImportConfig,
            config: data.updateImportConfig.configRaw ?? data.updateImportConfig.config,
          };
          return {
            data: record,
          };
        }
        throw new Error(`Could not update ${resource}`);
      },
    };
  }

if (resource === 'ImportConfig' && fetchType === 'CREATE') {
  // Ensure data is properly extracted from params
  const config = typeof params.data.config === 'string'
    ? JSON.parse(params.data.config)
    : params.data.config;

  const createImportConfigInput = {
    name: params.data.name,
    config: normalizeConfigEnums(config),
    configRaw: params.data.configRaw,
    projectId: params.data.project?.id || null,
    companyId: params.data.company?.id || null,
  };

  return {
    ...builtQuery,
    query: gql`
    mutation createImportConfig($createImportConfigInput: CreateImportConfigInput!) {
      data: createImportConfig(createImportConfigInput:$createImportConfigInput) {
        id
        name
        configRaw
        company {
          id
        }
        project {
          id
        }
        createdBy {
          id
        }
        createdAt
        updatedAt
      }
    }
`,
    variables: { createImportConfigInput },  // Make sure the variable name matches your GraphQL mutation
    parseResponse: ({ data }) => {
      if (data?.data) {
        const record = {
          ...data.data,
          config: data.data.configRaw ?? data.data.config,
        };
        return { data: record };
      }
      throw new Error('Could not create ImportConfig');
    },
  };
}

  if (resource === 'Translation' && fetchType === 'UPDATE') {
    return {
      // Use the default query variables and parseResponse
      ...builtQuery,
      // Override the query
      query: gql`
          mutation updateTranslation($id: Int!, $english: String, $de: String, $es: String, $fr: String, $th: String, $zh: String) {
              updateTranslation(id: $id, updateTranslationInput: {
                  english: $english,
                  de: $de,
                  es: $es,
                  fr: $fr,
                  th: $th,
                  zh: $zh,
              } ) {
                  id
                  english
                  de
                  es
                  fr
                  th
                  zh
              }
          }`,
      parseResponse: ({data}) => {
        if (data[`update${resource}`]) {
          return {
            data: {
              ...data[`update${resource}`],
            }
          };
        }

        throw new Error(`Could not update ${resource}`);
      },
    };
  }

  if (resource === 'Translation' && fetchType === 'DELETE') {
    return {
      // Use the default query variables and parseResponse
      ...builtQuery,
      // Override the query
      query: gql`
          mutation deleteTranslation($id: Int!) {
              deleteTranslation(id: $id)
          }`,
      parseResponse: ({data}) => {
        if (data[`deleteTranslation`] === 1) {
          return {
            data: {
              deleteTranslation: {
                id: params.id,
              }
            }
          };
        }

        throw new Error(`Could not delete ${resource}`);
      },
    };
  }

  if (resource === 'Translation' && fetchType === 'CREATE') {
    return {
      // Use the default query variables and parseResponse
      ...builtQuery,
      // Override the query
      query: gql`
          mutation createTranslation($english: String, $de: String, $es: String, $fr: String, $th: String, $zh: String) {
              createTranslation(createTranslationInput: {
                  english: $english,
                  de: $de,
                  es: $es,
                  fr: $fr,
                  th: $th,
                  zh: $zh,
              } ) {
                  id
                  english
                  de
                  es
                  fr
                  th
                  zh
              }
          }`,
      parseResponse: ({data}) => {
        if (data[`createTranslation`]) {
          return {
            data: {
              ...data[`createTranslation`],
            }
          };
        }

        throw new Error(`Could not create ${resource}`);
      },
    };
  }
  if (resource === 'Company' && fetchType === 'GET_ONE') {
    return {
      ...builtQuery,
      query: gql`
          query Company($id: Int) {
              data: Company(id: $id) {
                  id
                  name
                  type
                  location
                  company_trade
                  payment_method
                  acn
                  contact_name
                  contact_phone
                  contact_email
                  projects {
                      id
                      __typename
                  }
                  projectIds
                  groups {
                      id
                      __typename
                  }
                  reportTemplates {
                      id
                      __typename
                  }
                  created_at
                  updated_at
                  auto_approve
                  hubspot_id
                  bci_id
                  stripe_id
                  users {
                      id
                      __typename
                  }
                  usersCount
                  totalBillableRecords
                  totalCollaborators
                  billingEnforced
                  __typename
              }
          }

      `,
    }
  }

  if (resource === 'Company' && (fetchType === 'GET_LIST' || fetchType === 'GET_MANY')) {
    const {variables} = builtQuery;

    if (variables.filter) {
      const {useBillingFilter, ...rest} = variables.filter;

      // Clone the variables object to avoid modifying the original reference
      const newVariables = {
        ...variables,
        filter: rest, // Exclude billingFilter from the original filter object
        billingFilter: useBillingFilter ? {itemLimitExceeded: useBillingFilter} : null
      };

      return {
        ...builtQuery,
        variables: newVariables,
        query: gql`
            query allCompanies($page: Int, $perPage: Int, $sortField: String, $sortOrder: String, $filter: CompanyFilter, $billingFilter: BillingFilter) {
                items: allCompanies(page: $page, perPage: $perPage, sortField: $sortField, sortOrder: $sortOrder, filter: $filter, billingFilter: $billingFilter) {
                    id,
                    name,
                    company_trade,
                    auto_approve,
                    type,
                    location,
                    projectIds,
                    projects { id }
                    stripe_id
                    totalBillableRecords,
                    totalCollaborators,
                    billingEnforced,
                    stripeUsageLimits { items }
                }
                total: _allCompaniesMeta(filter: $filter, billingFilter: $billingFilter) {
                    count
                    __typename
                }
            }
        `,
      };
    }

    // If no modifications needed, return the original query
    return builtQuery;
  }
  if (resource === 'Company' && fetchType === 'UPDATE') {

    const input = {...params.data.updateCompanyInput};
    if (input?.location) {
      input.company_location = input.location;
      delete input.location;
    }
    if (input?.company_type) {
      input.company_type = input.type;
      delete input.company_type;
    }
    return {
      // Use the default query variables and parseResponse
      ...builtQuery,
      variables: {...params.data, updateCompanyInput: input},
      // Override the query
      query: gql`
          mutation updateCompany($id: Int!, $updateCompanyInput: UpdateCompanyInput!) {
              data: updateCompany(id: $id, updateCompanyInput: $updateCompanyInput) {
                  id
                  name
                  type
                  location
                  company_trade
                  payment_method
                  acn
                  contact_name
                  contact_phone
                  contact_email
                  projects {
                      id
                      __typename
                  }
                  projectIds
                  groups {
                      id
                      __typename
                  }
                  reportTemplates {
                      id
                      __typename
                  }
                  created_at
                  updated_at
                  auto_approve
                  hubspot_id
                  bci_id
                  stripe_id
                  users {
                      id
                      __typename
                  }
                  billingEnforced
                  __typename
              }
          }
      `,
      parseResponse: ({data}) => {
        if (data.data) {
          return {
            data: data.data
          };
        }
        throw new Error(`Could not update ${resource}`);
      },
    };
  }


  if (resource === 'ReportTemplate' && fetchType === 'UPDATE') {
    if (params.data.company) {
      params.data.company_id = Number(params.data.company.id);
      delete params.data['company.id'];
      delete params.data.company;
    }
    return {
      // Use the default query variables and parseResponse
      ...builtQuery,
      variables: params.data,
      // Override the query
      query: gql`
          mutation updateReportTemplate($id: Int!, $name: String, $template: String, $company_id: Int) {
              updateReportTemplate(id: $id, updateReportTemplateInput: {
                  name: $name,
                  template: $template,
                  company_id: $company_id,
              } ) {
                  id
                  name
                  template
                  company {
                      id
                  }
              }
          }`,
      parseResponse: ({data}) => {
        if (data[`update${resource}`]) {
          return {
            data: {
              ...data[`update${resource}`],
            }
          };
        }

        throw new Error(`Could not update ${resource}`);
      },
    };
  }

  if (resource === 'ReportTemplate' && fetchType === 'DELETE') {
    return {
      // Use the default query variables and parseResponse
      ...builtQuery,
      // Override the query
      query: gql`
          mutation deleteReportTemplate($id: Int!) {
              deleteReportTemplate(id: $id)
          }`,
      parseResponse: ({data}) => {
        if (data[`deleteReportTemplate`] === 1) {
          return {
            data: {
              deleteTranslation: {
                id: params.id,
              }
            }
          };
        }

        throw new Error(`Could not delete ${resource}`);
      },
    };
  }

  if (resource === 'ReportTemplate' && fetchType === 'CREATE') {
    return {
      // Use the default query variables and parseResponse
      ...builtQuery,
      // Override the query
      query: gql`
          mutation createReportTemplate(
              $name: String
              $template: String
              $company_id: Int
          ) {
              createReportTemplate(
                  createReportTemplateInput: {
                      name: $name
                      template: $template
                      company_id: $company_id
                  }
              ) {
                  id
                  name
                  template
                  company {
                      id
                  }
              }
          }`,
      parseResponse: ({data}) => {
        if (data[`createReportTemplate`]) {
          return {
            data: {
              ...data[`createReportTemplate`],
            }
          };
        }

        throw new Error(`Could not create ${resource}`);
      },
    };
  }
  if (resource === 'Description' && fetchType === 'GET_MANY') {
    return {
      ...builtQuery,
      query: gql`
          query allDescriptions($page: Int, $perPage: Int, $sortField: String, $sortOrder: String, $filter: DescriptionFilter) {
              allDescriptions(page: $page, perPage: $perPage, sortField: $sortField, sortOrder: $sortOrder, filter: $filter) {
                  id
                  recordType {
                      id
                      __typename
                  }
                  name
                  type
                  order
                  listValues {
                      id
                      value
                      __typename
                  }
                  listValuesCount
                  list
                  requiredQuantityFilters {
                      id
                      group_order
                      recordTypeFilters {
                          id
                          fk_filter_record_type_id
                          recordType {
                              id
                              __typename
                          }
                          __typename
                      }
                      listValueFilters {
                          id
                          fk_filter_description_id
                          fk_filter_list_value_id
                          listValue {
                              id
                              __typename
                          }
                          description {
                              id
                              __typename
                          }
                          __typename
                      }
                      textFilters {
                          id
                          fk_filter_description_id
                          value
                          description {
                              id
                              __typename
                          }
                          __typename
                      }
                      __typename
                  }
                  __typename
              }
          }`,
      parseResponse: (res) => {
        const data = {
          data: res.data.allDescriptions,
        };
        return data;
        throw new Error(`Could not load descriptions`);
      },
    }
  }

  if (resource === 'Description' && fetchType === 'GET_ONE') {
    return {
      ...builtQuery,
      query: gql`
          query Description($id: Int!) {
              Description(id: $id) {
                  id
                  recordType {
                      id
                      __typename
                  }
                  name
                  type
                  order
                  listValues {
                      id
                      value
                      __typename
                  }
                  listValuesCount
                  list
                  requiredQuantityFilters {
                      id
                      group_order
                      recordTypeFilters {
                          id
                          fk_filter_record_type_id
                          recordType {
                              id
                              __typename
                          }
                          __typename
                      }
                      listValueFilters {
                          id
                          fk_filter_description_id
                          fk_filter_list_value_id
                          listValue {
                              id
                              __typename
                          }
                          description {
                              id
                              __typename
                          }
                          __typename
                      }
                      textFilters {
                          id
                          fk_filter_description_id
                          value
                          description {
                              id
                              __typename
                          }
                          __typename
                      }
                      __typename
                  }
                  __typename
              }
          }
      `,
      parseResponse: (res) => {
        const data = {
          data: res.data.Description,
        };
        return data;
        throw new Error(`Could not load descriptions`);
      },
    }
  }

  if (resource === 'RuleDefinitionListValue' && fetchType === 'GET_LIST') {
    return {
      ...builtQuery,
      query: gql`
          query allRuleDefinitionListValues(
              $page: Int
              $perPage: Int
              $sortField: String
              $sortOrder: String
              $filter: RuleDefinitionListValueFilter
          ) {
              items: allRuleDefinitionListValues(
                  page: $page
                  perPage: $perPage
                  sortField: $sortField
                  sortOrder: $sortOrder
                  filter: $filter
              ) {
                  id
                  name
                  parentDescription {
                      id
                      __typename
                  }
                  parentListValue {
                      id
                      value
                      __typename
                  }
                  childDescription {
                      id
                      __typename
                  }
                  childListValue {
                      id
                      value
                      __typename
                  }
                  type
                  link_relationship
                  order
                  createdBy {
                      id
                      __typename
                  }
                  created_at
                  updatedBy {
                      id
                      __typename
                  }
                  updated_at
                  conditionDescription {
                      id
                      __typename
                  }
                  conditionListValue {
                      id
                      value
                      __typename
                  }
                  conditionRecordType {
                      id
                      __typename
                  }
                  actionDescription {
                      id
                      __typename
                  }
                  actionListValue {
                      id
                      value
                      __typename
                  }
                  actionRecordType {
                      id
                      __typename
                  }
                  project {
                      id
                      __typename
                  }
                  __typename
              }
              total: _allRuleDefinitionListValuesMeta(page: $page, perPage: $perPage, filter: $filter) {
                  count
                  __typename
              }
          }`,
      parseResponse: (res) => {
        const data = {
          data: res.data.items,
          total: res.data.total.count,
        };
        return data;
      },
    }
  }

  if (resource === 'ImportConfig' && fetchType === 'GET_ONE') {
    return {
      ...builtQuery,
      query: gql`query ImportConfig($id: Int!) {
        ImportConfig(id: $id) {
          id
          name
          configRaw
          company {
            id
            name
          }
          project {
            id
            name
          }
          createdBy {
            id
            username
            first_name
            last_name
            email
          }
          createdAt
          updatedAt
        }
   
      }
      `,
    parseResponse: (res) => {
      const record = res.data.ImportConfig;
      const normalizedRecord = record?.configRaw
        ? { ...record, config: record.configRaw }
        : { ...record };
      return {
        data: normalizedRecord,
      };
    },
  }
}
  if (resource === 'ImportConfig' && fetchType === 'GET_LIST') {

    console.log('get list Fetching all import configs');
    return {
      ...builtQuery,
      query: gql`query allImportConfigs($page: Int, $perPage: Int, $sortField: String, $sortOrder: String, $filter: ImportConfigFilter) {
        allImportConfigs(page: $page, perPage: $perPage, sortField: $sortField, sortOrder: $sortOrder, filter: $filter) {
          id
          name
          configRaw
          company {
            id
            name
          }
          project {
            id
            name
          }
          createdBy {
            id
            username
            first_name
            last_name
            email
          }
          createdAt
          updatedAt
        }
        total: _allImportConfigsMeta(filter: $filter) {
            count
        }
}
`,
    parseResponse: (res) => {
      const data = {
        data: res.data.allImportConfigs.map((record) => {
          if (record?.configRaw) {
            return {
              ...record,
              config: record.configRaw,
            };
          }
          return record;
        }),
        total: res.data.total.count,
      };
      return data;
    },
  }
  }


  if (fetchType === 'DELETE') {
    return {
      // Use the default query variables and parseResponse
      ...builtQuery,
      // Override the query
      query: gql`
          mutation delete${resource} ($id: Int!) {
              delete${resource}(id: $id)
          }`,
      parseResponse: ({data}) => {
        if (data[`delete${resource}`] === 1) {
          return {
            data: {
              [`delete${resource}`]: {
                id: params.id,
              }
            }
          };
        }

        throw new Error(`Could not delete ${resource}`);
      },
    };
  }

  return builtQuery;
};


export default () => buildGraphQLProvider({client, buildQuery: myBuildQuery});
